import { useMemo, useContext } from 'react';
import { translate } from '@utils/i18n';
import { hasGlobalTerritoriesApplied } from '../../../helpers/territories';
import { CalendarContext } from '../CalendarContext';
import FilterTypes from '../../../constants/FilterTypes';
const namespace = 'features.calendar.header.index';
const inspectionStatusTemplate = ({ label, icon, color }) => {
    return `<div style="display: flex; align-items: center">
            <span class="qmb-avatar--24--status-icon" style="display: flex; border-radius: 50%; align-items: center; justify-content: center">
              <i class="fa-lg ${icon}" style="color: ${color}" />
            </span>
            <div style="margin-left: 0.8rem;">${label}</div>
          </div>`;
};
const inspectionStatusValueTemplate = ({ label, icon, color }) => {
    return `<span class="qmb-avatar--24--status-icon">
            <i class="fa-lg ${icon}" style="color: ${color}" />
          </span> ${label}`;
};
export default function useFilters() {
    const { assetsForSelect, inspectionTypesForSelect, visitTypesForSelect, permissions, actions: { handleMultiSelectFilterChange, onToggleFilter, onTypeFilterChange, onTypeFilterToggle, onChangeReactFilter }, state: { filters: { active: filterActiveState, values: filterValuesState } } } = useContext(CalendarContext);
    const globalTerritoriesApplied = hasGlobalTerritoriesApplied();
    const visitTypeFilters = useMemo(() => {
        if (permissions.hasService)
            return [
                {
                    field: 'type',
                    locale: 'visit_type',
                    type: 'commonSelect',
                    active: filterActiveState.type,
                    locked: false,
                    optionsForSelect: visitTypesForSelect,
                    value: filterValuesState.type,
                    onChange: onTypeFilterChange(),
                    onToggle: onTypeFilterToggle()
                },
                {
                    field: 'inspectionType',
                    locale: 'inspection_type',
                    type: FilterTypes.GenericMultiSelect,
                    active: filterValuesState.type === 'Inspection',
                    locked: true,
                    optionsForSelect: inspectionTypesForSelect.map((opt) => ({ title: opt.label, value: opt.value })),
                    value: filterValuesState.inspectionType,
                    onChange: handleMultiSelectFilterChange('inspectionType'),
                    onToggle: onToggleFilter('inspectionType'),
                    tooltip: filterValuesState.type === 'Inspection' ? undefined : translate('inspection_type_tooltip', { namespace })
                }
            ];
        return [
            {
                field: 'inspectionStatus',
                locale: 'inspection_status',
                type: FilterTypes.GenericMultiSelect,
                disableSorting: true,
                backendField: 'status',
                placeholder: 'filters.all_except_cancelled',
                active: filterActiveState.inspectionStatus,
                value: filterValuesState.inspectionStatus,
                template: (dataItem) => inspectionStatusTemplate(dataItem),
                valueTemplate: (dataItem) => inspectionStatusValueTemplate(dataItem),
                onChange: handleMultiSelectFilterChange('inspectionStatus'),
                onToggle: onToggleFilter('inspectionStatus'),
                itemHeight: 40
            },
            {
                field: 'inspectionType',
                locale: 'inspection_type',
                type: FilterTypes.GenericMultiSelect,
                active: filterActiveState.inspectionType,
                optionsForSelect: inspectionTypesForSelect.map((opt) => ({ title: opt.label, value: opt.value })),
                value: filterValuesState.inspectionType,
                onChange: handleMultiSelectFilterChange('inspectionType'),
                onToggle: onToggleFilter('inspectionType')
            }
        ];
    }, [
        permissions,
        filterActiveState,
        filterValuesState,
        handleMultiSelectFilterChange,
        onToggleFilter,
        onTypeFilterChange,
        onTypeFilterToggle,
        inspectionTypesForSelect
    ]);
    const filters = useMemo(() => {
        var _a, _b;
        const filtersList = [
            {
                field: 'asset',
                locale: 'asset',
                type: FilterTypes.TreeMultiSelect,
                active: filterActiveState.asset,
                value: filterValuesState.asset,
                optionsForSelect: assetsForSelect,
                onChange: handleMultiSelectFilterChange('asset'),
                onToggle: onToggleFilter('asset')
            },
            {
                field: 'building',
                locale: 'building',
                type: FilterTypes.GenericMultiSelect,
                active: filterActiveState.building,
                locked: false,
                value: filterValuesState.building,
                onChange: handleMultiSelectFilterChange('building'),
                onToggle: onToggleFilter('building')
            },
            {
                field: 'account',
                locale: 'account',
                type: FilterTypes.GenericMultiSelect,
                active: filterActiveState.account,
                value: filterValuesState.account,
                onChange: handleMultiSelectFilterChange('account'),
                onToggle: onToggleFilter('account')
            },
            {
                field: 'technicianTeam',
                locale: 'team',
                type: FilterTypes.GenericMultiSelect,
                active: filterActiveState.technicianTeam,
                locked: false,
                value: filterValuesState.technicianTeam,
                onChange: handleMultiSelectFilterChange('technicianTeam'),
                onToggle: onToggleFilter('technicianTeam')
            },
            {
                field: 'tags',
                backendField: 'tags',
                locale: 'inspection_tags',
                type: FilterTypes.GenericMultiSelect,
                active: filterActiveState.tags,
                locked: false,
                value: filterValuesState.tags,
                onChange: handleMultiSelectFilterChange('tags'),
                onToggle: onToggleFilter('tags')
            },
            {
                field: 'buildingTags',
                backendField: 'tags',
                locale: 'building_tags',
                type: FilterTypes.GenericMultiSelect,
                active: filterActiveState.buildingTags,
                value: filterValuesState.buildingTags,
                onChange: handleMultiSelectFilterChange('buildingTags'),
                onToggle: onToggleFilter('buildingTags')
            },
            {
                field: 'accountTags',
                backendField: 'tags',
                locale: 'account_tags',
                type: FilterTypes.GenericMultiSelect,
                active: filterActiveState.accountTags,
                value: filterValuesState.accountTags,
                onChange: handleMultiSelectFilterChange('accountTags'),
                onToggle: onToggleFilter('accountTags')
            },
            {
                field: 'workOrderTags',
                backendField: 'tags',
                locale: 'work_order_tags',
                type: FilterTypes.GenericMultiSelect,
                active: filterActiveState.workOrderTags,
                value: filterValuesState.workOrderTags,
                onChange: handleMultiSelectFilterChange('workOrderTags'),
                onToggle: onToggleFilter('workOrderTags')
            },
            {
                field: 'statuses',
                locale: 'status',
                type: 'statuses',
                active: filterActiveState.statuses,
                locked: false,
                inspectionStatusKeys: (_a = filterValuesState.inspectionStatusKeys) !== null && _a !== void 0 ? _a : [],
                visitStatusKeys: (_b = filterValuesState.visitStatusKeys) !== null && _b !== void 0 ? _b : [],
                defaultInspectionStatusKeys: [],
                defaultVisitStatusKeys: [],
                onInspectionStatusChange: onChangeReactFilter('inspectionStatusKeys'),
                onVisitStatusChange: onChangeReactFilter('visitStatusKeys'),
                onToggle: onToggleFilter('statuses')
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ].concat(visitTypeFilters);
        if (!globalTerritoriesApplied) {
            filtersList.push({
                field: 'territory',
                locale: 'territory',
                type: FilterTypes.GenericMultiSelect,
                active: filterActiveState.territory,
                locked: false,
                value: filterValuesState.territory,
                withUnassigned: true,
                onChange: handleMultiSelectFilterChange('territory'),
                onToggle: onToggleFilter('territory')
            });
        }
        filtersList.push({
            field: 'additionalTerritory',
            backendField: 'additional_territory',
            locale: 'additional_territory',
            type: FilterTypes.GenericMultiSelect,
            active: filterActiveState.additionalTerritory,
            value: filterValuesState.additionalTerritory,
            onChange: handleMultiSelectFilterChange('additionalTerritory'),
            onToggle: onToggleFilter('additionalTerritory')
        });
        return filtersList;
    }, [filterActiveState, filterValuesState, globalTerritoriesApplied, visitTypeFilters]);
    return filters;
}
